import React, {useState, useEffect} from 'react'
import axios from 'axios'
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Autocomplete,
} from '@mui/material'
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import {
  allergyOptions,
  codeStatusOptions,
  infoSourceOptions,
  precautionOptions,
  medicationOptions,
  woundsOptions,
  alternativeNutritionOptions,
  weightdetailsOptions,
  weightamountOptions,
  dietaryDetailsOptions,
  initialStateForm1,
} from './NURSES_ASSESSMENT_FORM_CONSTANTS'
import SearchableSelect from '../../../../../reusable-components/forms/SearchableSelect';

function CheckboxGroup({ label, options, value, onChange }) {
  return (
    <FormControl component="fieldset" sx={{ mb: 2 }}>
      <Typography variant="subtitle2">{label}</Typography>
      <FormGroup row>
        {options.map((opt, i) => (
          <FormControlLabel
            key={opt.value}
            control={
              <Checkbox
                checked={value.includes(opt.value)}
                onChange={e => onChange(opt.value, e.target.checked)}
              />
            }
            label={opt.label}
            sx={{mr: 1, width: 300}}
          />
        ))}
      </FormGroup>
    </FormControl>
  );
}

function NursesAssessmentform1({data = {}, setFormValues, onSubmit, buttons, isEdit = false}) {
  const [formState, setFormState] = useState({...initialStateForm1, ...data})
  const [clients, setClients] = useState([])
  useEffect(() => {
    setFormState((prev) => ({...prev, ...data}))
  }, [data])

  const handleChange = (name, value) => {
    setFormState((prev) => {
      const updated = {...prev, [name]: value}
      setFormValues && setFormValues(updated)
      return updated
    })
  }

  const handleCheckboxChange = (name, optionValue, checked) => {
    setFormState((prev) => {
      const arr = Array.isArray(prev[name]) ? [...prev[name]] : []
      if (checked) {
        if (!arr.includes(optionValue)) arr.push(optionValue)
      } else {
        const idx = arr.indexOf(optionValue)
        if (idx > -1) arr.splice(idx, 1)
      }
      const updated = {...prev, [name]: arr}
      setFormValues && setFormValues(updated)
      return updated
    })
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    onSubmit && onSubmit(formState)
  }
  useEffect(() => {
    const fetchClients = async () => {
      try {
        const response = await axios.get('/clients/getClientNames')
        const {data} = response?.data
        const updatedClients = data.map((client) => ({label: client.fullName, value: client._id}))
        setClients(updatedClients)
      } catch (error) {
        console.error('Error fetching clients:', error)
      }
    }
    fetchClients()
  }, [])
  const handleSearchableSelectChange = (name, newValue) => {
    if (newValue) {
      handleChange(name, newValue.value)
    } else {
      handleChange(name, null)
    }
  }
  return (
    <Box component='form' onSubmit={handleSubmit} sx={{mt: 2}}>
      <Box>
        <Box sx={{display: 'flex', gap: 2, mb: 2}}>
          <SearchableSelect
          label="Client"
          options={clients}
          value={formState.clientId || null}
          onChange={(newValue) => {
            handleSearchableSelectChange('clientId', newValue)
          }}
          disabled={isEdit}
          fullWidth={true}
          required={false}
          sx={{flex: 1}}
        />
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Assessment Date"
              value={formState.assessmentDate ? dayjs(formState.assessmentDate) : null}
              onChange={(newValue) => {
                handleChange('assessmentDate', newValue ? newValue.format('MM-DD-YYYY') : '');
              }}
              slotProps={{
                textField: {
                  size: 'small',
                  style: { width: '100%' },
                  InputProps: {
                    readOnly: true,
                    style: { cursor: 'pointer' },
                  },
                  onClick: (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const iconButton = e.currentTarget.querySelector('[data-testid="OpenPickerIconButton"]') ||
                      e.currentTarget.querySelector('.MuiIconButton-root');
                    if (iconButton) {
                      (iconButton as HTMLElement).click();
                    }
                  },
                },
              }}
              sx={{
                flex: 1,
                '& .MuiInputBase-input': {
                  WebkitTextFillColor: '#000000',
                  cursor: 'pointer',
                },
                '& .MuiOutlinedInput-root': {
                  cursor: 'pointer',
                },
                '& .MuiIconButton-root': {
                  cursor: 'pointer',
                },
                '& .MuiPaper-root': {
                  boxShadow: '0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  marginTop: '4px',
                },
                '@media (max-width: 600px)': {
                  '& .MuiPickersPopper-paper': {
                    position: 'fixed !important',
                    top: 'auto !important',
                    left: '50% !important',
                    transform: 'translateX(-50%) !important',
                    width: 'calc(100% - 32px) !important',
                    maxWidth: '360px !important',
                    margin: '0 auto !important',
                  },
                },
              }}
              format="MM-DD-YYYY"
              disableFuture={true}
              disableOpenPicker={false}
            />
          </LocalizationProvider>
        </Box>
        <Typography variant='subtitle1' sx={{mt: 2, mb: 1, fontSize: '16px', fontWeight: 'bold'}} color='primary'>
          Allergies Details
        </Typography>
        <CheckboxGroup
          label='Allergies'
          options={allergyOptions}
          value={formState.allergies}
          onChange={(val, checked) => handleCheckboxChange('allergies', val, checked)}
        />
        <TextField
          label='Listed allergies'
          value={formState.otherAllergies}
          onChange={(e) => handleChange('otherAllergies', e.target.value)}
          fullWidth
          sx={{mb: 2}}
          multiline
          minRows={2}
        />
        <Box sx={{display: 'flex', gap: 2, mb: 2}}>
          <FormControl fullWidth size="small">
            <InputLabel>Code Status</InputLabel>
            <Select
              value={formState.codeStatus}
              label='Code Status'
              onChange={(e) => handleChange('codeStatus', e.target.value)}
              size="small"
            >
              {codeStatusOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel>Information Source</InputLabel>
            <Select
              value={formState.infoSource}
              label='Information Source'
              onChange={(e) => handleChange('infoSource', e.target.value)}
              size="small"
            >
              {infoSourceOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <TextField
          label='Info Source Details'
          value={formState.otherInfoSource}
          onChange={(e) => handleChange('otherInfoSource', e.target.value)}
          fullWidth
          sx={{mb: 2}}
          multiline
          minRows={2}
        />
        <FormControl fullWidth sx={{mb: 2}} size="small">
          <InputLabel>Precaution</InputLabel>
          <Select
            value={formState.precautions}
            label='Precaution'
            onChange={(e) => handleChange('precautions', e.target.value)}
            size="small"
          >
            {precautionOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label='Details'
          value={formState.otherPrecautions}
          onChange={(e) => handleChange('otherPrecautions', e.target.value)}
          fullWidth
          sx={{mb: 2}}
          multiline
          minRows={2}
        />

        <Typography variant='subtitle1' sx={{mt: 2, mb: 1, fontSize: '16px', fontWeight: 'bold'}} color='primary'>
          Vital Signs
        </Typography>
        <Box sx={{display: 'flex', gap: 2, mb: 2}}>
          <TextField
            label='Temperature (F)'
            value={formState.t}
            onChange={(e) => handleChange('t', e.target.value)}
            sx={{flex: 1}}
          />
          <TextField
            label='Respiratory Rate'
            value={formState.r}
            onChange={(e) => handleChange('r', e.target.value)}
            sx={{flex: 1}}
          />
          <TextField
            label='Pulse'
            value={formState.p}
            onChange={(e) => handleChange('p', e.target.value)}
            sx={{flex: 1}}
          />
        </Box>
        <Box sx={{display: 'flex', gap: 2, mb: 2}}>
          <TextField
            label='Blood Pressure (120/80)'
            value={formState.bp}
            onChange={(e) => handleChange('bp', e.target.value)}
            sx={{flex: 1}}
          />
          <TextField
            label='Height (ft)'
            value={formState.height}
            onChange={(e) => handleChange('height', e.target.value)}
            sx={{flex: 1}}
          />
          <TextField
            label='Weight (lbs)'
            value={formState.weight}
            onChange={(e) => handleChange('weight', e.target.value)}
            sx={{flex: 1}}
          />
        </Box>

        <Typography variant='subtitle1' sx={{mt: 2, mb: 1, fontSize: '16px', fontWeight: 'bold'}} color='primary'>
          Medications
        </Typography>
        <CheckboxGroup
          label='Medications'
          options={medicationOptions}
          value={formState.medications}
          onChange={(val, checked) => handleCheckboxChange('medications', val, checked)}
        />

        <Typography variant='subtitle1' sx={{mt: 2, mb: 1, fontSize: '16px', fontWeight: 'bold'}} color='primary'>
          Dietary Screen
        </Typography>
        <Box sx={{display: 'flex', gap: 2, mb: 2}}>
          <FormControl fullWidth size="small">
            <InputLabel>Wounds</InputLabel>
            <Select
              value={formState.wounds}
              label='Wounds'
              onChange={(e) => handleChange('wounds', e.target.value)}
              size="small"
            >
              {woundsOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel>Alternative Nutrition</InputLabel>
            <Select
              value={formState.alternativeNutrition}
              label='Alternative Nutrition'
              onChange={(e) => handleChange('alternativeNutrition', e.target.value)}
              size="small"
            >
              {alternativeNutritionOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box sx={{display: 'flex', gap: 2, mb: 2}}>
          <FormControl fullWidth size="small">
            <InputLabel>Weight Gained/Loss</InputLabel>
            <Select
              value={formState.weightdetails}
              label='Weight Gained/Loss'
              onChange={(e) => handleChange('weightdetails', e.target.value)}
              size="small"
            >
              {weightdetailsOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel>Amount Gain/Loss (lbs)</InputLabel>
            <Select
              value={formState.weightamount}
              label='Amount Gain/Loss (lbs)'
              onChange={(e) => handleChange('weightamount', e.target.value)}
              size="small"
            >
              {weightamountOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <CheckboxGroup
          label='Dietary Details'
          options={dietaryDetailsOptions}
          value={formState.dietaryDetails}
          onChange={(val, checked) => handleCheckboxChange('dietaryDetails', val, checked)}
        />
      </Box>
    </Box>
  )
}

export default NursesAssessmentform1
